import {
  Box, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  Typography, AppBar, Toolbar, IconButton, Avatar, Chip, Badge, Divider,
} from '@mui/material';
import {
  Dashboard, Business, ConfirmationNumber, Logout, AdminPanelSettings,
  CreditCard, Domain, Palette, BarChart, History, Notifications, Settings,
} from '@mui/icons-material';
import { Outlet, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../auth/authSlice';
import api from '../../api/axios';

const DRAWER_WIDTH = 240;

const navItems = [
  { label: 'Dashboard', icon: <Dashboard />, path: '/superadmin' },
  { divider: true, label: 'Clients' },
  { label: 'Tenants', icon: <Business />, path: '/superadmin/tenants' },
  { label: 'Billing', icon: <CreditCard />, path: '/superadmin/billing' },
  { label: 'Plans', icon: <BarChart />, path: '/superadmin/plans' },
  { divider: true, label: 'Platform' },
  { label: 'Domains', icon: <Domain />, path: '/superadmin/domains' },
  { label: 'Usage', icon: <BarChart />, path: '/superadmin/usage' },
  { label: 'Support Tickets', icon: <ConfirmationNumber />, path: '/superadmin/tickets' },
  { divider: true, label: 'System' },
  { label: 'Audit Logs', icon: <History />, path: '/superadmin/audit' },
  { label: 'Notifications', icon: <Notifications />, path: '/superadmin/notifications' },
  { label: 'Settings', icon: <Settings />, path: '/superadmin/settings' },
];

export default function SuperAdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { user, refreshToken } = useSelector(s => s.auth);

  // Guard: only super_admin role allowed
  if (!user || user.role !== 'super_admin') {
    return <Navigate to="/superadmin/login" replace />;
  }

  const handleLogout = async () => {
    try { await api.post('/auth/logout', { refresh_token: refreshToken }); } catch {}
    dispatch(logout());
    navigate('/superadmin/login');
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', overflow: 'hidden' }}>
      <AppBar position="fixed" sx={{ zIndex: t => t.zIndex.drawer + 1, bgcolor: '#1e1b4b' }} elevation={0}>
        <Toolbar>
          <AdminPanelSettings sx={{ mr: 1.5 }} />
          <Typography variant="h6" fontWeight={700} sx={{ flex: 1 }}>POS Pro — Super Admin</Typography>
          <Chip label="Super Admin" color="warning" size="small" sx={{ mr: 2 }} />
          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.light', fontSize: 13, mr: 1 }}>{user?.name?.[0]?.toUpperCase()}</Avatar>
          <IconButton color="inherit" onClick={handleLogout}><Logout /></IconButton>
        </Toolbar>
      </AppBar>

      <Drawer variant="permanent" sx={{ width: DRAWER_WIDTH, flexShrink: 0, '& .MuiDrawer-paper': { width: DRAWER_WIDTH, top: 64, height: 'calc(100% - 64px)', bgcolor: '#0f0c29', color: 'white', borderRight: 'none', overflowX: 'hidden' } }}>
        <List sx={{ px: 1, pt: 2 }}>
          {navItems.map((item, idx) => {
            if (item.divider) return (
              <Box key={idx} sx={{ px: 2, pt: 2, pb: 0.5 }}>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', fontSize: 10 }}>{item.label}</Typography>
                <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mt: 0.5 }} />
              </Box>
            );
            const active = item.path === '/superadmin' ? location.pathname === '/superadmin' : location.pathname.startsWith(item.path);
            return (
              <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton onClick={() => navigate(item.path)} sx={{ borderRadius: 2, bgcolor: active ? 'rgba(99,102,241,0.3)' : 'transparent', color: active ? 'white' : 'rgba(255,255,255,0.6)', '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' } }}>
                  <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: 14, fontWeight: active ? 600 : 400 }} />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, bgcolor: '#f8fafc', minHeight: '100vh', minWidth: 0 }}>
        <Toolbar />
        <Box sx={{ p: 3 }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
